import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { Sparkles } from 'lucide-react';

import defaultProfileImg from '../../assets/images/default_profile_img.svg';
import { ReactComponent as NoJobIcon } from '../../assets/images/no-job-icon.svg';
import EmptyActivityIcon from '../../assets/svgComponents/EmptyActivityIcon';
import { ActivityItem } from '../../components/Dashboard/ActivityItem/ActivityItem';
import { ContactImportCard } from '../../components/Dashboard/ContactImportCard/ContactImportCard';
import DashboardLoader from '../../components/Dashboard/DashboardLoader/DashboardLoader';
import { Leaderboard } from '../../components/Dashboard/Leaderboard/Leaderboard';
import { MobileJobFeed } from '../../components/Dashboard/MobileJobFeed/MobileJobFeed';
import { RecommendationItem } from '../../components/Dashboard/RecommendationItem/RecommendationItem';
import { ReferralProgramDetails } from '../../components/Dashboard/ReferralProgramDetails/ReferralProgramDetails';
import { StatsOverview } from '../../components/Dashboard/StatsOverview/StatsOverview';
import Header from '../../components/shared/Header/Header';
import SeeMoreButton from '../../components/shared/SeeMoreButton/SeeMoreButton';
import {
  getActivityList,
  getHotJobs,
  getRecommendedJobs,
  getReferralRecommendations,
  getTermsAndConditions,
} from '../../redux/actions/dashboardActions';
import urls from '../../routing/urls';
import {
  selectCurrentUser,
  selectCurrentUserLoading,
  selectCurrentUserOrganizations,
  selectUserStats,
} from '../../selectors/authSelectors';
import {
  selectActivityList,
  selectActivityListLoading,
  selectHotJobs,
  selectHotJobsLoading,
  selectLeaderboardListLoading,
  selectRecommendedJobs,
  selectRecommendedJobsLoading,
  selectReferralLink,
  selectReferralLinkLoading,
  selectReferralRecommendations,
  selectReferralRecommendationsLoading,
  selectReferralRecommendationsTotal,
  selectTermsAndConditions,
  selectTermsAndConditionsLoading,
} from '../../selectors/dashboardSelectors';
import { selectCurrentOrganization, selectCurrentOrganizationLoading } from '../../selectors/organizationSelectors';
import JobSection from '../Browse/OpeningsListElement/JobSection';

import styles from '../Browse/OpeningsListElement/OpeningsListElement.module.scss';

const Dashboard = () => {
  const { t } = useTranslation('dashboard');
  const dispatch = useDispatch();
  const [recommendationsDisplayCount] = useState(2);
  const [recommendedJobsDisplayCount] = useState(3);
  const [isLoading, setIsLoading] = useState(true);

  const currentOrganization = useSelector(selectCurrentOrganization);
  const user = useSelector(selectCurrentUser);
  const stats = useSelector(selectUserStats);
  const termsAndConditions = useSelector(selectTermsAndConditions);
  const hotJobs = useSelector(selectHotJobs);
  const recommendedJobs = useSelector(selectRecommendedJobs);
  const recommendedJobsLoading = useSelector(selectRecommendedJobsLoading);
  const referralRecommendations = useSelector(selectReferralRecommendations);
  const activityList = useSelector(selectActivityList);
  const currentUserOrganizations = useSelector(selectCurrentUserOrganizations);
  const referralLink = useSelector(selectReferralLink);
  const referralRecommendationsTotal = useSelector(selectReferralRecommendationsTotal);

  const isActivityListLoading = useSelector(selectActivityListLoading);
  const termsAndConditionsLoading = useSelector(selectTermsAndConditionsLoading);
  const referralLinkLoading = useSelector(selectReferralLinkLoading);
  const referralRecommendationsLoading = useSelector(selectReferralRecommendationsLoading);
  const hotJobsLoading = useSelector(selectHotJobsLoading);
  const leaderboardListLoading = useSelector(selectLeaderboardListLoading);
  const currentOrganizationLoading = useSelector(selectCurrentOrganizationLoading);
  const loading = useSelector((state) => state.globalReducer.loading);
  const currentUserLoading = useSelector(selectCurrentUserLoading);
  const aiRecommendationsEnabled = currentOrganization?.ai_recommendations_for_members !== false;
  const recommendations =
    referralRecommendations?.length > 0 && referralRecommendations.some((recommendation) => recommendation.job_data);

  const renderActivities = () =>
    activityList.map((activity, index) => {
      const uniqueKey = `${activity.id}-${index}`;

      if (index === activityList.length - 1) {
        return (
          <div key={uniqueKey}>
            <ActivityItem activity={activity} />
          </div>
        );
      }
      return <ActivityItem key={uniqueKey} activity={activity} />;
    });

  useEffect(() => {
    localStorage.removeItem('activeTab');
    localStorage.removeItem('referralProviderData');
  }, []);

  useEffect(() => {
    setIsLoading(
      isActivityListLoading ||
        termsAndConditionsLoading ||
        referralLinkLoading ||
        referralRecommendationsLoading ||
        hotJobsLoading ||
        leaderboardListLoading ||
        currentOrganizationLoading ||
        loading ||
        currentUserLoading ||
        recommendedJobsLoading
    );
  }, [
    isActivityListLoading,
    termsAndConditionsLoading,
    referralLinkLoading,
    referralRecommendationsLoading,
    hotJobsLoading,
    leaderboardListLoading,
    currentOrganizationLoading,
    loading,
    currentUserLoading,
    recommendedJobsLoading,
  ]);

  useEffect(() => {
    if (currentOrganization) {
      if (currentOrganization.ai_recommendations_for_members !== false) {
        dispatch(getReferralRecommendations({ limit: recommendationsDisplayCount }));
      }
      if (currentOrganization.name === 'Boon') {
        dispatch(getHotJobs({ boon_view: true }));
        dispatch(getRecommendedJobs({ boon_view: true }));
      } else {
        dispatch(getTermsAndConditions(currentOrganization.id));
        dispatch(getHotJobs({ organization_id: currentOrganization.id }));
        dispatch(getRecommendedJobs({ organization_id: currentOrganization.id }));
      }
      dispatch(getActivityList({ organization_id: currentOrganization.id }));
    }
  }, [dispatch, currentOrganization?.id]);

  const renderReferralsSection = () => {
    if (user?.contacts_count === 0 || !recommendations) {
      return <ContactImportCard contacts_count={user?.contacts_count} recommendations={!recommendations} />;
    }

    return (
      <div className="space-y-4">
        {referralRecommendations?.length > 0 &&
          referralRecommendations
            .filter((recommendation) => recommendation.job_data)
            .slice(0, recommendationsDisplayCount)
            .map((recommendation, index) => (
              <RecommendationItem
                key={recommendation.id || `recommendation-${index}`}
                recommendation={recommendation}
                currentOrganization={currentOrganization}
              />
            ))}
      </div>
    );
  };

  const renderActivitiesSection = () => (
    <div className="pb-4 bg-white rounded-xl shadow-card">
      <p className="p-3 pb-2 text-lg font-semibold mb-0">{t('activityFeed')}</p>
      {activityList.length > 0 ? (
        <div
          className="overflow-y-auto max-h-[690px] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
          style={{ scrollBehavior: 'smooth' }}
          onScroll={(e) => {
            if (!e || !e.currentTarget) return;
            const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
            const scrollThreshold = 150;
            const hasReachedThreshold = scrollHeight - scrollTop - clientHeight < scrollThreshold;

            if (!isActivityListLoading && hasReachedThreshold && activityList.length > 0) {
              clearTimeout(e.currentTarget.scrollTimeout);
              e.currentTarget.scrollTimeout = setTimeout(() => {
                dispatch(
                  getActivityList({
                    organization_id: currentOrganization.id,
                    offset: activityList.length,
                  })
                );
              }, 300);
            }
          }}
        >
          <div className="divide-y divide-gray-100">
            {renderActivities()}
            {isActivityListLoading && (
              <div className="flex justify-center p-4">
                <div className="w-8 h-8 border-t-2 border-b-2 rounded-full animate-spin border-emerald-500" />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="p-4 text-center text-gray-500">
          <EmptyActivityIcon />
          <p className="text-[#0E1829] font-medium text-base mt-4 mb-0">{t('noRecentActivity')}</p>
        </div>
      )}
    </div>
  );

  return (
    <>
      <Header title={t('title')} />
      {isLoading ? (
        <DashboardLoader />
      ) : (
        <div className="h-[calc(100vh-84px)] max-md:h-[calc(100vh-134px)] overflow-y-scroll bg-gray-50 p-[26px] max-w-[1340px] 2xl:max-w-[1560px] mx-auto">
          <div className="flex gap-[16px] max-xl:flex-col w-full">
            <div className="space-y-4 w-full xl:w-[275px] xl:min-w-[275px] 2xl:w-[345px] 2xl:min-w-[345px]">
              <div className="flex flex-row items-center justify-between p-3 bg-white rounded-xl shadow-card">
                <div className="flex items-center gap-3 mb-0 text-left">
                  <img
                    src={user?.avatar_thumb || defaultProfileImg}
                    alt={user?.full_name}
                    className="object-cover w-8 h-8 rounded-full"
                  />
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-900 mb-0">{user?.full_name}</p>
                    <p className="text-xs text-gray-600 mb-0">
                      {currentOrganization &&
                        currentUserOrganizations?.find((org) => org.organization.id === currentOrganization.id)?.title}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-center">
                  <SeeMoreButton label={t('view')} tab="/account/profile" path={urls.profile} />
                </div>
              </div>
              <div className="hidden xl:block">{renderActivitiesSection()}</div>

              {!termsAndConditionsLoading && termsAndConditions?.referral_program_details && (
                <div className="hidden xl:block">
                  <ReferralProgramDetails termsAndConditions={termsAndConditions} />
                </div>
              )}
            </div>
            <div className="flex max-lg:flex-col w-full gap-[16px]">
              <div className="w-full">
                <div className="bg-white rounded-xl shadow-card">
                  <p className="w-full p-3 pb-0 text-lg font-semibold text-gray-900 mb-0">{t('yourFeed')}</p>
                  <hr className="w-full text-gray-400 my-[14px]" />
                  <div className="space-y-4">
                    {aiRecommendationsEnabled && (
                      <div className="hidden px-3 lg:block">
                        <div className="space-y-4">
                          <div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5">
                                <Sparkles className="w-4 h-4 text-amber-500" />
                                <p className="text-base font-semibold text-gray-900 mb-0">{t('referralsForYou')}</p>
                              </div>
                              {referralRecommendationsTotal > 0 &&
                                recommendations &&
                                recommendationsDisplayCount < referralRecommendationsTotal && (
                                  <div className="flex gap-2">
                                    <SeeMoreButton label={t('seeMore')} path="/recommendations" tab="/dashboard" />
                                  </div>
                                )}
                            </div>
                          </div>
                          {renderReferralsSection()}
                        </div>
                      </div>
                    )}
                    <div className="hidden px-3 pb-3 lg:block">
                      {recommendedJobs?.job_recommendations?.length > 0 ? (
                        <div className="space-y-3">
                          <div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5">
                                <Sparkles className="w-4 h-4 text-amber-500" />
                                <p className="text-base font-semibold text-gray-900 mb-0">{t('recommendedJobs')}</p>
                              </div>
                              {recommendedJobs.total_count > recommendedJobsDisplayCount && (
                                <SeeMoreButton label={t('seeMore')} path="/recommended-jobs" tab="/dashboard" />
                              )}
                            </div>
                          </div>
                          <div className="space-y-4">
                            {recommendedJobs.job_recommendations
                              .slice(0, recommendedJobsDisplayCount)
                              .map((job, index) => (
                                <div
                                  key={job.job_id ? `job-${job.job_id}` : `job-${index}`}
                                  className={classNames(styles.openingItemParent)}
                                  role="presentation"
                                >
                                  <JobSection
                                    job={job.job_data}
                                    matchScore={job.match_score}
                                    referralLink={referralLink}
                                  />
                                </div>
                              ))}
                          </div>
                        </div>
                      ) : (
                        hotJobs?.jobs?.length > 0 && (
                          <div className="space-y-3">
                            <div>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1.5">
                                  <Sparkles className="w-4 h-4 text-amber-500" />
                                  <p className="text-base font-semibold text-gray-900 mb-0">{t('recommendedJobs')}</p>
                                </div>
                                {hotJobs.jobs.length > recommendedJobsDisplayCount && (
                                  <SeeMoreButton label={t('seeMore')} path="/jobs" tab="/browse" />
                                )}
                              </div>
                            </div>
                            <div className="space-y-4">
                              {hotJobs.jobs.slice(0, recommendedJobsDisplayCount).map((job, index) => (
                                <div
                                  key={job.id || `hot-job-${index}`}
                                  className={classNames(styles.openingItemParent)}
                                  role="presentation"
                                >
                                  <JobSection job={job} referralLink={referralLink} />
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                    <div className="block pb-4 lg:hidden">
                      <MobileJobFeed />
                    </div>
                  </div>
                  {referralRecommendations?.length === 0 &&
                    hotJobs?.jobs?.length === 0 &&
                    recommendedJobs?.job_recommendations?.length === 0 && (
                      <div className="p-4 text-center md:p-8">
                        <NoJobIcon />
                        <p className="text-[#0E1829] font-medium text-base mb-0">{t('noMatchingJobs')}</p>
                      </div>
                    )}
                </div>
              </div>
              <div className="block lg:hidden">{renderActivitiesSection()}</div>
              <div className="space-y-4 w-full lg:w-[275px] lg:min-w-[275px] 2xl:w-[345px] 2xl:min-w-[345px]">
                <StatsOverview currentOrganization={currentOrganization} currentUser={user} stats={stats} />
                <div>
                  <Leaderboard />
                </div>
              </div>
            </div>
            <div className="block xl:hidden max-lg:hidden">{renderActivitiesSection()}</div>
            {!termsAndConditionsLoading && termsAndConditions?.referral_program_details && (
              <div className="block xl:hidden md:col-span-2">
                <ReferralProgramDetails termsAndConditions={termsAndConditions} />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Dashboard;
